// src/pages/Wallet/hooks/usePullToRefresh.js
import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook to handle pull-to-refresh functionality
 * Provides touch event handlers and manages refresh state
 */
export const usePullToRefresh = (onRefresh) => {
  const scrollContainerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(null);
  const lastTouchY = useRef(null);
  const refreshThreshold = 70; // Distance required to trigger refresh
  const refreshingRef = useRef(false);
  const animationFrameRef = useRef(null);

  const handleTouchStart = (e) => {
    // Only initiate if we're at the top of the scroll
    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      lastTouchY.current = e.touches[0].clientY;
      
      // Cancel any existing animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const handleTouchMove = (e) => {
    // Skip handling if refresh is in progress
    if (isRefreshing || refreshingRef.current) return;
    
    // Only handle if we've started tracking touch
    if (touchStartY.current === null) return;
    
    // Get current scroll position
    const scrollTop = scrollContainerRef.current ? scrollContainerRef.current.scrollTop : 0;
    
    // Only apply pull-to-refresh if we're at the top of the content
    if (scrollTop > 0) return;
    
    const currentY = e.touches[0].clientY;
    lastTouchY.current = currentY;
    
    // Calculate the pull distance with resistance (square root for non-linear resistance)
    const rawDistance = currentY - touchStartY.current;
    
    if (rawDistance > 0) {
      // Apply resistance - the further you pull, the harder it gets
      const distance = Math.sqrt(rawDistance) * 5;
      
      // Set the pull distance, which will adjust the spacer height
      setPullDistance(distance);
      
      // Prevent default browser behavior when pulling down
      if (distance > 10) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    // Skip if refresh is already in progress
    if (isRefreshing || refreshingRef.current) return;
    
    // If pull distance is greater than threshold, trigger refresh
    if (pullDistance > refreshThreshold) {
      refreshData();
    } else {
      // Otherwise, just reset pull distance with animation
      animateReset();
    }
    
    // Reset touch tracking
    touchStartY.current = null;
    lastTouchY.current = null;
  };
  
  // Animate the reset of pull distance
  const animateReset = () => {
    if (pullDistance <= 0) return;
    
    const newDistance = pullDistance * 0.85;
    
    if (newDistance < 1) {
      setPullDistance(0);
      return;
    }
    
    setPullDistance(newDistance);
    animationFrameRef.current = requestAnimationFrame(animateReset);
  };

  // Refresh function
  const refreshData = useCallback(async () => {
    if (refreshingRef.current) return;
    
    refreshingRef.current = true;
    setIsRefreshing(true);
    
    try {
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      // Add a small delay to show the loading indicator
      setTimeout(() => {
        setIsRefreshing(false);
        setPullDistance(0);
        refreshingRef.current = false;
      }, 800);
    }
  }, [onRefresh]);

  // Clean up any animation frames on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    scrollContainerRef,
    pullDistance,
    isRefreshing,
    refreshThreshold,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    refreshData
  };
};